import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Fade,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { Peer } from 'peerjs';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { ChevronLeft } from '@mui/icons-material';
import {
  FeedbackSnackbar,
  ProgressModal,
  UploadState,
  uploadToScreenApp,
} from './common';

// Add monitoring intervals map
const monitoringIntervals = new WeakMap<any, number>();

interface PeerConnection {
  peerId: string;
  call: any;
  stream: MediaStream;
  dataConnection?: any;
}

interface VideoPlayerProps {
  localStream: MediaStream | null;
  remoteStreams: PeerConnection[];
  isVideoEnabled?: boolean;
  isAudioEnabled?: boolean;
  isRecording?: boolean;
  isHost?: boolean;
  meetingUrl?: string;
  joinId?: string;
  isConnecting?: boolean;
  connectionStatus?: string;
  onToggleVideo?: () => void;
  onToggleAudio?: () => void;
  onCopyUrl?: () => void;
  onJoinIdChange?: (id: string) => void;
  onJoin?: () => void;
  onEndCall?: () => void;
}

// Generate a YouTube-like ID (11 characters)
const generateYouTubeLikeId = (): string => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  return Array.from({ length: 11 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  localStream,
  remoteStreams,
  isVideoEnabled,
  isAudioEnabled,
  isRecording,
  isHost,
  meetingUrl,
  joinId,
  isConnecting,
  connectionStatus,
  onToggleVideo,
  onToggleAudio,
  onCopyUrl,
  onJoinIdChange,
  onJoin,
  onEndCall,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRefs = useRef<HTMLVideoElement[]>([]);
  const animationRef = useRef<number>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Set up local video stream
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.onloadedmetadata = () => {
        localVideoRef.current?.play().catch((error) => {
          console.error('Error playing local video:', error);
        });
      };
    }
  }, [localStream]);

  // Set up remote video streams
  useEffect(() => {
    remoteStreams.forEach((peer, index) => {
      if (remoteVideoRefs.current[index]) {
        remoteVideoRefs.current[index].srcObject = peer.stream;
        remoteVideoRefs.current[index].onloadedmetadata = () => {
          remoteVideoRefs.current[index]?.play().catch((error) => {
            console.error('Error playing remote video:', error);
          });
        };
      }
    });
  }, [remoteStreams]);

  // Canvas drawing effect
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Set canvas dimensions to match container while maintaining aspect ratio
    canvas.width = 1280; // Base width
    canvas.height = 720; // Base height (16:9 aspect ratio)

    const drawCanvas = (): void => {
      if (!canvas || !ctx) return;

      // Clear canvas with black background
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Collect all video streams to display
      const allStreams = [];

      // Add local stream first if available
      if (localVideoRef.current) {
        allStreams.push({
          element: localVideoRef.current,
          isLocal: true,
        });
      }

      // Add remote streams
      remoteStreams
        .map((_, index) => remoteVideoRefs.current[index])
        .filter(
          (el): el is HTMLVideoElement =>
            el !== null && el.readyState === el.HAVE_ENOUGH_DATA
        )
        .forEach((el) => {
          allStreams.push({
            element: el,
            isLocal: false,
          });
        });

      // If no streams, return
      if (allStreams.length === 0) {
        // If we have local video but it's not ready yet, try again
        if (localVideoRef.current) {
          animationRef.current = requestAnimationFrame(drawCanvas);
        }
        return;
      }

      // Calculate grid dimensions
      const cols = Math.ceil(Math.sqrt(allStreams.length));
      const rows = Math.ceil(allStreams.length / cols);

      // Calculate tile dimensions
      const tileWidth = canvas.width / cols;
      const tileHeight = canvas.height / rows;

      // Function to calculate contained dimensions
      const getContainedDimensions = (
        videoElement: HTMLVideoElement,
        maxWidth: number,
        maxHeight: number
      ) => {
        const videoRatio = videoElement.videoWidth / videoElement.videoHeight;
        const containerRatio = maxWidth / maxHeight;
        let width = maxWidth;
        let height = maxHeight;

        if (videoRatio > containerRatio) {
          // Video is wider than container
          width = maxWidth;
          height = width / videoRatio;
        } else {
          // Video is taller than container
          height = maxHeight;
          width = height * videoRatio;
        }

        // Calculate position to center the video in the tile
        const x = (maxWidth - width) / 2;
        const y = (maxHeight - height) / 2;

        return { width, height, x, y };
      };

      // Draw all streams
      allStreams.forEach((stream, index) => {
        const row = Math.floor(index / cols);
        const col = index % cols;
        const tileX = col * tileWidth;
        const tileY = row * tileHeight;

        const { width, height, x, y } = getContainedDimensions(
          stream.element,
          tileWidth,
          tileHeight
        );

        // Draw all videos normally without mirroring
        ctx.drawImage(stream.element, tileX + x, tileY + y, width, height);
      });

      animationRef.current = requestAnimationFrame(drawCanvas);
    };

    drawCanvas();

    // eslint-disable-next-line consistent-return
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [remoteStreams]);

  // Add window close warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = t('p2pMeeting.messages.closeWarning');
        return e.returnValue;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording, t]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16/9',
        minHeight: { xs: 'auto', sm: 400 },
        maxHeight: '80vh',
        bgcolor: 'black',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Connection Status Overlay */}
      {(isConnecting ||
        (connectionStatus &&
          connectionStatus !== t('p2pMeeting.messages.waitingForPeer'))) && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: 3,
            borderRadius: 2,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              marginBottom: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isConnecting && (
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor:
                    'primary.main transparent primary.main primary.main',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                  display: 'inline-block',
                  marginRight: 1,
                }}
              />
            )}
            {connectionStatus}
          </Typography>
        </Box>
      )}

      {/* Hidden video element for local stream */}
      <video
        ref={localVideoRef}
        autoPlay
        playsInline
        muted
        style={{ display: 'none' }}
      >
        <track kind="captions" />
      </video>

      {/* Hidden video elements for remote streams */}
      {remoteStreams.map((_, index) => (
        <video
          key={index}
          ref={(el) => {
            if (el) remoteVideoRefs.current[index] = el;
          }}
          autoPlay
          playsInline
          style={{ display: 'none' }}
        >
          <track kind="captions" />
        </video>
      ))}

      {/* Canvas for remote streams grid */}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />

      {/* Controls overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 8, sm: 16 },
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          padding: { xs: 1, sm: 1.5 },
          borderRadius: 3,
          backdropFilter: 'blur(10px)',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        {/* URL Input - only show when hosting or when not yet connected */}
        {(isHost || (!isHost && !remoteStreams.length)) && (
          <Box
            sx={{
              minWidth: 200,
              maxWidth: 400,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isHost && remoteStreams.length === 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'white', whiteSpace: 'nowrap' }}
              >
                Share link to start:
              </Typography>
            )}
            <TextField
              size="small"
              value={isHost ? meetingUrl : joinId}
              placeholder={t('p2pMeeting.join.placeholder')}
              onChange={(e) => onJoinIdChange?.(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                readOnly: isHost,
                endAdornment: isHost ? (
                  <Tooltip
                    title={t('p2pMeeting.tooltips.copyLinkToStart')}
                    arrow
                    placement="top"
                  >
                    <IconButton
                      onClick={onCopyUrl}
                      edge="end"
                      sx={{
                        color: 'white',
                        '& svg': { fill: 'white' },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={onJoin}
                    disabled={!joinId}
                    sx={{
                      color: 'white',
                      minWidth: 'auto',
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {t('p2pMeeting.join.button')}
                  </Button>
                ),
              }}
            />
          </Box>
        )}

        {/* Video Toggle */}
        <IconButton
          onClick={onToggleVideo}
          title={t(
            isVideoEnabled
              ? 'p2pMeeting.controls.video.disable'
              : 'p2pMeeting.controls.video.enable'
          )}
          sx={{
            color: 'white',
            '& svg': { fill: 'white' },
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          {isVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
        </IconButton>

        {/* Audio Toggle */}
        <IconButton
          onClick={onToggleAudio}
          title={t(
            isAudioEnabled
              ? 'p2pMeeting.controls.audio.disable'
              : 'p2pMeeting.controls.audio.enable'
          )}
          sx={{
            color: 'white',
            '& svg': { fill: 'white' },
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
        </IconButton>

        {/* Recording Indicator */}
        {isRecording && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'error.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                  '100%': { opacity: 1 },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                fontWeight: 500,
                letterSpacing: 0.5,
              }}
            >
              {t('p2pMeeting.messages.recording')}
            </Typography>
          </Box>
        )}

        {/* End Call Button - only show when connected to peers */}
        {remoteStreams.length > 0 && (
          <IconButton
            onClick={onEndCall}
            title={t('p2pMeeting.controls.endCall')}
            sx={{
              color: 'white',
              '& svg': { fill: 'white' },
              '&:hover': {
                backgroundColor: 'error.main',
              },
            }}
          >
            <CallEndIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const P2PMeeting: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Add recording state and refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });

  const [myPeerId, setMyPeerId] = useState<string>('');
  const [isHost, setIsHost] = useState<boolean>(true);
  const [joinId, setJoinId] = useState<string>('');
  const [isVideoEnabled, setIsVideoEnabled] = useState<boolean>(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState<boolean>(true);
  const [isStreamReady, setIsStreamReady] = useState<boolean>(false);
  const [isPeerReady, setIsPeerReady] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const peerRef = useRef<Peer | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const [peerConnections, setPeerConnections] = useState<PeerConnection[]>([]);
  const [connectedPeerIds, setConnectedPeerIds] = useState<string[]>([]);
  const dataConnectionsRef = useRef<{ [key: string]: any }>({});

  // Add new states for connection status
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('');

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const startLocalStream = async () => {
    console.log('Starting local stream...');
    setConnectionStatus(t('p2pMeeting.messages.initializingMedia'));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: isVideoEnabled,
        audio: isAudioEnabled,
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      localStreamRef.current = stream;
      setIsStreamReady(true);
      console.log('Local stream ready');
      return stream;
    } catch (error) {
      console.error('Failed to start local stream:', error);
      showSnackbar(t('p2pMeeting.messages.noMediaAccess'), 'error');
      return null;
    }
  };

  const monitorPeerConnection = (call: any, peerId: string) => {
    console.log('Starting connection monitoring for peer:', peerId);
    const checkConnection = () => {
      const { peerConnection } = call;
      if (!peerConnection) {
        console.log('No peer connection found for:', peerId);
        setPeerConnections((prev) => prev.filter((p) => p.peerId !== peerId));
        return false;
      }

      const state = peerConnection.connectionState;
      const iceState = peerConnection.iceConnectionState;
      console.log('Connection states for peer:', peerId, { state, iceState });

      if (
        state === 'closed' ||
        state === 'failed' ||
        state === 'disconnected' ||
        iceState === 'disconnected' ||
        iceState === 'failed' ||
        iceState === 'closed'
      ) {
        console.log('Peer disconnected:', peerId, { state, iceState });
        setPeerConnections((prev) => prev.filter((p) => p.peerId !== peerId));
        return false;
      }
      return true;
    };

    const intervalId = window.setInterval(() => {
      if (!checkConnection()) {
        clearInterval(intervalId);
        monitoringIntervals.delete(call);
      }
    }, 1000);

    monitoringIntervals.set(call, intervalId);
    return intervalId;
  };

  const stopMonitoring = (call: any) => {
    const intervalId = monitoringIntervals.get(call);
    if (intervalId) {
      clearInterval(intervalId);
      monitoringIntervals.delete(call);
    }
  };

  const broadcastToPeers = (message: any) => {
    Object.values(dataConnectionsRef.current).forEach((conn) => {
      if (conn && conn.open) {
        conn.send(message);
      }
    });
  };

  const connectToNewPeer = async (peerId: string) => {
    if (!peerRef.current || !localStreamRef.current || peerId === myPeerId) {
      return;
    }

    console.log('Connecting to new peer:', peerId);
    try {
      // Establish data connection first
      const dataConnection = peerRef.current.connect(peerId);
      dataConnectionsRef.current[peerId] = dataConnection;

      dataConnection.on('open', () => {
        console.log('Data connection established with:', peerId);
      });

      // Then establish media connection
      const call = peerRef.current.call(peerId, localStreamRef.current);

      call.on('stream', (remoteStream) => {
        console.log('Received remote stream from new peer:', peerId);
        setPeerConnections((prev) => {
          if (prev.some((p) => p.peerId === peerId)) {
            return prev;
          }
          const newConnections = [
            ...prev,
            {
              peerId,
              call,
              stream: remoteStream,
              dataConnection,
            },
          ];
          monitorPeerConnection(call, peerId);
          return newConnections;
        });
      });

      call.on('close', () => {
        console.log('Call closed for peer:', peerId);
        stopMonitoring(call);
        setPeerConnections((prev) => prev.filter((p) => p.peerId !== peerId));
        delete dataConnectionsRef.current[peerId];
      });

      call.on('error', (error) => {
        console.error('Call error:', error);
        stopMonitoring(call);
        setPeerConnections((prev) => prev.filter((p) => p.peerId !== peerId));
        delete dataConnectionsRef.current[peerId];
        showSnackbar(t('p2pMeeting.messages.peerError'), 'error');
      });
    } catch (error) {
      console.error('Connect error:', error);
      showSnackbar(t('p2pMeeting.messages.peerError'), 'error');
    }
  };

  const initializePeer = () => {
    console.log('Initializing peer...');
    setConnectionStatus(t('p2pMeeting.messages.initializingConnection'));
    const peer = new Peer(generateYouTubeLikeId());

    peer.on('open', (id) => {
      console.log('Peer connection open, ID:', id);
      setMyPeerId(id);
      setIsPeerReady(true);
      setConnectionStatus('');
      showSnackbar(t('p2pMeeting.messages.connectionEstablished'), 'success');
    });

    peer.on('connection', (dataConnection) => {
      const peerId = dataConnection.peer;
      console.log('Received data connection from:', peerId);
      dataConnectionsRef.current[peerId] = dataConnection;

      dataConnection.on('data', (data: any) => {
        console.log('Received data from peer:', peerId, data);
        if (data.type === 'PEER_LIST') {
          // Connect to all peers we're not connected to yet
          const newPeers = data.peers.filter(
            (id: string) =>
              id !== myPeerId &&
              !peerConnections.some((conn) => conn.peerId === id)
          );
          newPeers.forEach(connectToNewPeer);
        }
      });

      dataConnection.on('open', () => {
        console.log('Data connection opened with:', peerId);
        // If we're the host, send the current peer list
        if (isHost) {
          dataConnection.send({
            type: 'PEER_LIST',
            peers: [myPeerId, ...connectedPeerIds],
          });
        }
      });
    });

    peer.on('call', async (call) => {
      console.log('Receiving call from:', call.peer);
      if (localStreamRef.current) {
        call.answer(localStreamRef.current);

        call.on('stream', (remoteStream) => {
          console.log('Received remote stream from:', call.peer);
          setPeerConnections((prev) => {
            if (prev.some((p) => p.peerId === call.peer)) {
              return prev;
            }
            const newConnections = [
              ...prev,
              {
                peerId: call.peer,
                call,
                stream: remoteStream,
                dataConnection: dataConnectionsRef.current[call.peer],
              },
            ];
            monitorPeerConnection(call, call.peer);

            // Update connected peer IDs
            setConnectedPeerIds((current) => {
              if (!current.includes(call.peer)) {
                return [...current, call.peer];
              }
              return current;
            });

            // If we're the host, broadcast the updated peer list
            if (isHost) {
              broadcastToPeers({
                type: 'PEER_LIST',
                peers: [myPeerId, ...newConnections.map((c) => c.peerId)],
              });
            }

            return newConnections;
          });
        });

        call.on('close', () => {
          console.log('Call closed from peer:', call.peer);
          stopMonitoring(call);
          setPeerConnections((prev) =>
            prev.filter((p) => p.peerId !== call.peer)
          );
        });

        call.on('error', (error) => {
          console.error('Call error:', error);
          stopMonitoring(call);
          setPeerConnections((prev) =>
            prev.filter((p) => p.peerId !== call.peer)
          );
          showSnackbar(t('p2pMeeting.messages.peerError'), 'error');
        });
      }
    });

    peer.on('disconnected', () => {
      console.log('Peer disconnected, attempting to reconnect...');
      setConnectionStatus(t('p2pMeeting.messages.reconnecting'));
      peer.reconnect();
    });

    peer.on('close', () => {
      console.log('Peer connection closed');
      setConnectionStatus(t('p2pMeeting.messages.meetingEnded'));
      // Clear all monitoring intervals
      peerConnections.forEach((conn) => {
        stopMonitoring(conn.call);
      });
      setPeerConnections([]);
    });

    peer.on('error', (error) => {
      console.error('Peer error:', error);
      setConnectionStatus('');
      showSnackbar(
        t('p2pMeeting.messages.connectionError') + error.message,
        'error'
      );
    });

    peerRef.current = peer;
    return peer;
  };

  const connectToPeer = async () => {
    if (!peerRef.current || !localStreamRef.current || !joinId) {
      console.log('Cannot connect, missing:', {
        peer: !peerRef.current,
        stream: !localStreamRef.current,
        joinId: !joinId,
      });
      return;
    }

    setIsConnecting(true);
    setConnectionStatus(t('p2pMeeting.messages.connecting'));
    console.log('Connecting to host peer:', joinId);

    try {
      // First establish data connection
      const dataConnection = peerRef.current.connect(joinId);
      dataConnectionsRef.current[joinId] = dataConnection;

      dataConnection.on('open', () => {
        console.log('Data connection established with host');
      });

      dataConnection.on('data', (data: any) => {
        console.log('Received data from host:', data);
        if (data.type === 'PEER_LIST') {
          // Connect to all peers we're not connected to yet
          const newPeers = data.peers.filter(
            (id: string) =>
              id !== myPeerId &&
              !peerConnections.some((conn) => conn.peerId === id)
          );
          newPeers.forEach(connectToNewPeer);
        }
      });

      // Then establish media connection
      const call = peerRef.current.call(joinId, localStreamRef.current);

      call.on('stream', (remoteStream) => {
        console.log('Received remote stream from host');
        setIsConnecting(false);
        setConnectionStatus('');
        setPeerConnections((prev) => {
          if (prev.some((p) => p.peerId === joinId)) {
            return prev;
          }
          const newConnections = [
            ...prev,
            {
              peerId: joinId,
              call,
              stream: remoteStream,
              dataConnection,
            },
          ];
          showSnackbar(t('p2pMeeting.messages.peerJoined'), 'success');
          monitorPeerConnection(call, joinId);
          return newConnections;
        });
      });

      call.on('close', () => {
        console.log('Call closed for peer:', joinId);
        stopMonitoring(call);
        setPeerConnections((prev) => prev.filter((p) => p.peerId !== joinId));
        showSnackbar(t('p2pMeeting.messages.peerLeft'), 'error');
      });

      call.on('error', (error) => {
        console.error('Call error:', error);
        setIsConnecting(false);
        setConnectionStatus('');
        stopMonitoring(call);
        setPeerConnections((prev) => prev.filter((p) => p.peerId !== joinId));
        showSnackbar(t('p2pMeeting.messages.peerError'), 'error');
      });
    } catch (error) {
      console.error('Connect error:', error);
      setIsConnecting(false);
      setConnectionStatus('');
      showSnackbar(t('p2pMeeting.messages.peerError'), 'error');
    }
  };

  // First effect: Check URL parameters and handle auto-join
  useEffect(() => {
    // Check for ?id= in the URL
    const params = new URLSearchParams(location.search);
    const meetingId = params.get('id');

    if (meetingId) {
      console.log('Found meeting ID in URL:', meetingId);
      setIsHost(false);
      setJoinId(meetingId);

      // Initialize directly as a joiner
      const initialize = async () => {
        console.log('Starting initialization as joiner...');
        const stream = await startLocalStream();
        if (!stream) return;

        // Initialize peer without generating an ID
        const peer = new Peer();
        peerRef.current = peer;

        peer.on('open', () => {
          console.log('Peer connection open, ready to join');
          setIsPeerReady(true);
        });

        peer.on('error', (error) => {
          console.error('Peer error:', error);
          showSnackbar(
            t('p2pMeeting.messages.connectionError') + error.message,
            'error'
          );
        });
      };

      initialize();
    }
  }, [location.search]);

  // Initialize as host
  useEffect(() => {
    if (!isHost || joinId) return undefined; // Skip if we're not host or have a join ID

    const initialize = async () => {
      console.log('Starting initialization as host...');
      const stream = await startLocalStream();
      if (!stream) return;
      initializePeer();
    };

    initialize();

    // Cleanup function
    return () => {
      localStreamRef.current?.getTracks().forEach((track) => track.stop());
      peerRef.current?.destroy();
      setPeerConnections([]);
    };
  }, [isHost, joinId]); // Only run for host initialization

  // Auto-join effect
  useEffect(() => {
    const isReady = isStreamReady && isPeerReady;
    console.log('Checking auto-join conditions:', {
      isStreamReady,
      isPeerReady,
      isHost,
      joinId,
      peerConnections: peerConnections.length,
    });

    if (isReady && !isHost && joinId && peerConnections.length === 0) {
      console.log('Auto-joining meeting:', joinId);
      connectToPeer();
    }
  }, [isStreamReady, isPeerReady, isHost, joinId, peerConnections.length]);

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getVideoTracks();
      const newState = !isVideoEnabled;
      tracks.forEach((track) => {
        const trackRef = track;
        trackRef.enabled = newState;
      });
      setIsVideoEnabled(newState);
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getAudioTracks();
      const newState = !isAudioEnabled;
      tracks.forEach((track) => {
        const trackRef = track;
        trackRef.enabled = newState;
      });
      setIsAudioEnabled(newState);
    }
  };

  // Function to get the full URL
  const getFullUrl = (id: string) => {
    // Get the current URL path up to /tool/p2pmeeting
    const currentUrl = window.location.href;
    const baseUrlMatch = currentUrl.match(/(.*\/tool\/p2pmeeting)/);
    // eslint-disable-next-line prefer-template
    const baseUrl = baseUrlMatch
      ? baseUrlMatch[1]
      : `${window.location.origin}/#/tool/p2pmeeting`;
    return `${baseUrl}?id=${id}`;
  };

  // Update copy to clipboard function to copy full URL
  const copyToClipboard = async () => {
    try {
      const fullUrl = getFullUrl(myPeerId);
      await navigator.clipboard.writeText(fullUrl);
      showSnackbar(t('p2pMeeting.messages.copied'), 'success');
    } catch (err) {
      showSnackbar(t('p2pMeeting.messages.copyError'), 'error');
    }
  };

  // Add recording functions
  const startRecording = (): void => {
    const canvas = document.querySelector('canvas');
    if (!canvas) {
      console.log('Cannot start recording: No canvas element found');
      return;
    }

    if (!localStreamRef.current) {
      console.log('Cannot start recording: No local stream available');
      return;
    }

    console.log('Starting recording...');
    try {
      // Get canvas stream with fallback for iOS
      let canvasStream;
      try {
        canvasStream = canvas.captureStream(30); // 30 FPS
      } catch (error) {
        console.log('Canvas capture not supported, using fallback method');
        // Fallback for iOS: create a virtual stream from the canvas
        const ctx = canvas.getContext('2d');
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');

        // Create a video stream from the canvas frames
        canvasStream = new MediaStream();
        const drawFrame = () => {
          if (!ctx || !tempCtx) return;
          tempCtx.drawImage(canvas, 0, 0);
          requestAnimationFrame(drawFrame);
        };
        drawFrame();

        // Add the canvas as a video track
        try {
          const track = (tempCanvas as any)
            .captureStream(30)
            .getVideoTracks()[0];
          canvasStream.addTrack(track);
        } catch (e) {
          console.error('Failed to create video track:', e);
          return;
        }
      }

      // Create audio context for mixing
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const destination = audioContext.createMediaStreamDestination();

      // Function to add an audio track to the mix
      const addAudioToMix = (stream: MediaStream) => {
        stream.getAudioTracks().forEach((track) => {
          if (track.enabled) {
            // Only add enabled tracks
            const source = audioContext.createMediaStreamSource(
              new MediaStream([track])
            );
            source.connect(destination);
            console.log('Added audio track to mix');
          }
        });
      };

      // Add local audio to mix
      if (localStreamRef.current) {
        addAudioToMix(localStreamRef.current);
        console.log('Added local audio to mix');
      }

      // Add remote audio to mix
      peerConnections.forEach((peer) => {
        if (peer.stream) {
          addAudioToMix(peer.stream);
          console.log('Added remote audio from peer:', peer.peerId);
        }
      });

      // Create a new stream that combines canvas video with mixed audio
      const mixedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);

      // Check for supported MIME types
      const mimeTypes = [
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4',
        'video/x-matroska;codecs=avc1',
      ];

      const selectedMimeType =
        mimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || '';

      if (!selectedMimeType) {
        throw new Error('No supported MIME type found for recording');
      }

      const mediaRecorder = new MediaRecorder(mixedStream, {
        mimeType: selectedMimeType,
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
          console.log('Recorded chunk size:', event.data.size);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, processing chunks...');
        const blob = new Blob(recordedChunksRef.current, {
          type: selectedMimeType,
        });
        console.log('Created blob of size:', blob.size);

        // Upload the recording
        try {
          const fileExtension = selectedMimeType.includes('mp4')
            ? 'mp4'
            : 'webm';
          await uploadToScreenApp(
            blob,
            `meeting-recording-${Date.now()}.${fileExtension}`,
            () => {
              console.log('Starting upload...');
              setUploadState((prev) => ({ ...prev, uploading: true }));
            },
            (progress) => {
              console.log('Upload progress:', progress);
              setUploadState((prev) => ({ ...prev, progress }));
            },
            () => {
              console.log('Upload complete');
              setUploadState((prev) => ({
                ...prev,
                uploadComplete: true,
                uploading: false,
              }));
              showSnackbar(
                t('p2pMeeting.messages.recordingUploaded'),
                'success'
              );
            },
            (error) => {
              console.error('Upload error:', error);
              setUploadState((prev) => ({ ...prev, error, uploading: false }));
              showSnackbar(t('p2pMeeting.messages.uploadError'), 'error');
            },
            false
          );
        } catch (error) {
          console.error('Failed to upload recording:', error);
          showSnackbar(t('p2pMeeting.messages.uploadError'), 'error');
        }

        // Clean up audio context
        try {
          await audioContext.close();
        } catch (error) {
          console.error('Error closing audio context:', error);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Record in 1-second chunks
      console.log('Recording started with MIME type:', selectedMimeType);
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      showSnackbar(t('p2pMeeting.messages.recordingError'), 'error');
    }
  };

  const stopRecording = () => {
    console.log('Stopping recording...');
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        console.log('Recording stopped successfully');
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    } else {
      console.log('No active recording to stop');
    }
  };

  // Modify peer connections effect to handle recording
  useEffect(() => {
    console.log('Peer connections changed:', {
      peerCount: peerConnections.length,
      isRecording,
    });

    // Start recording when first peer joins
    if (peerConnections.length === 1 && !isRecording) {
      console.log('First peer joined, starting recording');
      startRecording();
    }
    // Stop recording when last peer leaves
    else if (peerConnections.length === 0 && isRecording) {
      console.log('Last peer left, stopping recording');
      stopRecording();
    }
  }, [peerConnections.length, isRecording]);

  // Add cleanup
  useEffect(() => {
    return () => {
      if (isRecording) {
        console.log('Component unmounting, stopping recording');
        stopRecording();
      }
    };
  }, [isRecording]);

  const [joinModalOpen, setJoinModalOpen] = useState(false);

  const cleanupConnections = () => {
    // Stop all media tracks
    localStreamRef.current?.getTracks().forEach((track) => track.stop());

    // Close all peer connections and their associated media tracks
    peerConnections.forEach((conn) => {
      conn.stream.getTracks().forEach((track) => track.stop());
      conn.call.close();
      stopMonitoring(conn.call);
      if (conn.dataConnection && conn.dataConnection.open) {
        conn.dataConnection.close();
      }
    });

    // Close all data connections
    Object.values(dataConnectionsRef.current).forEach((conn) => {
      if (conn && conn.open) {
        conn.close();
      }
    });

    // Clear all references
    dataConnectionsRef.current = {};
    setPeerConnections([]);
    setConnectedPeerIds([]);

    // Destroy peer connection
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }
  };

  // Update cleanup effect to use the new cleanup function
  useEffect(() => {
    return (): void => {
      cleanupConnections();
    };
  }, []);

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1, sm: 2, md: 4 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {location.state?.fromMediaActions && (
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ChevronLeft />}
            onClick={() => {
              if (isRecording) {
                const shouldLeave = window.confirm(
                  'You have an active recording. Are you sure you want to leave? The recording will be stopped.'
                );
                if (!shouldLeave) return;
              }
              navigate(-1);
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Back to Library
          </Button>
        </Box>
      )}
      <Fade in timeout={800}>
        <Box>
          <VideoPlayer
            localStream={localStreamRef.current}
            remoteStreams={peerConnections}
            isVideoEnabled={isVideoEnabled}
            isAudioEnabled={isAudioEnabled}
            isRecording={isRecording}
            isHost={isHost}
            meetingUrl={myPeerId ? getFullUrl(myPeerId) : ''}
            joinId={joinId}
            isConnecting={isConnecting}
            connectionStatus={connectionStatus}
            onToggleVideo={toggleVideo}
            onToggleAudio={toggleAudio}
            onCopyUrl={copyToClipboard}
            onJoinIdChange={(id) => setJoinId(id)}
            onJoin={connectToPeer}
            onEndCall={() => {
              if (isRecording) {
                stopRecording();
              }
              cleanupConnections();
              setConnectionStatus(t('p2pMeeting.messages.meetingEnded'));
              navigate(location.pathname);
            }}
          />

          {/* Join Meeting Modal */}
          <Dialog
            open={joinModalOpen}
            onClose={() => setJoinModalOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
              },
            }}
          >
            <DialogTitle>
              {isHost ? t('p2pMeeting.host.title') : t('p2pMeeting.join.title')}
            </DialogTitle>
            <DialogContent>
              {isHost ? (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('p2pMeeting.host.shareLink')}
                  </Typography>
                  <TextField
                    fullWidth
                    value={myPeerId ? getFullUrl(myPeerId) : ''}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <IconButton onClick={copyToClipboard} edge="end">
                          <ContentCopyIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('p2pMeeting.join.enterLink')}
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={t('p2pMeeting.join.placeholder')}
                    value={joinId}
                    onChange={(e) => setJoinId(e.target.value)}
                    variant="outlined"
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsHost(!isHost)}>
                {isHost
                  ? t('p2pMeeting.host.joinOther')
                  : t('p2pMeeting.join.hostNew')}
              </Button>
              {!isHost && (
                <Button
                  variant="contained"
                  onClick={() => {
                    connectToPeer();
                    setJoinModalOpen(false);
                  }}
                  disabled={!joinId}
                >
                  {t('p2pMeeting.join.button')}
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {uploadState.uploading && (
            <ProgressModal
              open={uploadState.uploading}
              progress={uploadState.progress}
              isComplete={uploadState.uploadComplete}
              processingMessage={t('p2pMeeting.messages.uploadingRecording')}
              completeMessage={t('p2pMeeting.messages.recordingUploaded')}
            />
          )}
        </Box>
      </Fade>

      <FeedbackSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Container>
  );
};

export default P2PMeeting;
